import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Container,
  Heading,
  Spinner,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import { OpenAPI } from "../client"

type UnsubscribeResponse = {
  status: string
  lead?: {
    email?: string
    first_name?: string
    last_name?: string
  }
}

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
})

async function publicFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${OpenAPI.BASE}${path}`)
  if (!res.ok) {
    const text = await res.text()
    try {
      const parsed = JSON.parse(text)
      throw new Error(
        parsed?.error ||
          parsed?.detail ||
          parsed?.message ||
          `HTTP ${res.status}`,
      )
    } catch {
      throw new Error(text || `HTTP ${res.status}`)
    }
  }
  return (await res.json()) as T
}

function UnsubscribePage() {
  const { token } = Route.useSearch()
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const mutedText = useColorModeValue("gray.600", "gray.300")

  const unsubscribeQuery = useQuery({
    queryKey: ["public", "unsubscribe", token],
    queryFn: () =>
      publicFetch<UnsubscribeResponse>(
        `/public/unsubscribe?token=${encodeURIComponent(token)}`,
      ),
    enabled: Boolean(token),
    retry: false,
  })

  return (
    <Container maxW="lg" py={{ base: 10, md: 20 }}>
      <Box
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="2xl"
        p={{ base: 6, md: 8 }}
        bg={panelBg}
        boxShadow="lg"
      >
        <VStack align="stretch" spacing={5}>
          <Box>
            <Heading size="lg">Wypisz się z newslettera</Heading>
            <Text mt={2} color={mutedText}>
              Ta strona potwierdza i realizuje Twoją rezygnację z otrzymywania
              przyszłych wiadomości marketingowych.
            </Text>
          </Box>

          {!token ? (
            <Alert status="warning" borderRadius="lg">
              <AlertIcon />
              <Box>
                <AlertTitle>Brak tokenu</AlertTitle>
                <AlertDescription>
                  Link do rezygnacji z subskrypcji jest niekompletny. Otwórz
                  oryginalną wiadomość e-mail i spróbuj ponownie.
                </AlertDescription>
              </Box>
            </Alert>
          ) : unsubscribeQuery.isPending ? (
            <VStack py={8} spacing={4}>
              <Spinner size="lg" />
              <Text color={mutedText}>
                Przetwarzamy Twoją prośbę o rezygnację z subskrypcji...
              </Text>
            </VStack>
          ) : unsubscribeQuery.isError ? (
            <Alert status="error" borderRadius="lg">
              <AlertIcon />
              <Box>
                <AlertTitle>Żądanie nie powiodło się</AlertTitle>
                <AlertDescription>
                  {unsubscribeQuery.error instanceof Error
                    ? unsubscribeQuery.error.message
                    : "Nie udało się zrealizować rezygnacji z subskrypcji."}
                </AlertDescription>
              </Box>
            </Alert>
          ) : (
            <Alert status="success" borderRadius="lg">
              <AlertIcon />
              <Box>
                <AlertTitle>
                  Wypisanie się zakończyło się powodzeniem
                </AlertTitle>
                <AlertDescription>
                  {unsubscribeQuery.data?.lead?.email
                    ? `${unsubscribeQuery.data.lead.email} został wykluczony z przyszłych kampanii.`
                    : "Twój adres e-mail został wykluczony z przyszłych kampanii."}
                </AlertDescription>
              </Box>
            </Alert>
          )}
        </VStack>
      </Box>
    </Container>
  )
}
